import React from "react";

const PrivacyPolicy = () => (
  <div className="min-h-screen pt-28 sm:pt-32 bg-gray-900 text-white px-4 sm:px-6 lg:px-8 py-8 max-w-4xl mx-auto">
    <h1 className="text-4xl md:text-5xl font-extrabold mb-6 text-teal-400 border-b border-teal-600 pb-4">
      Privacy Policy
    </h1>
    <p className="text-sm text-gray-400 mb-6">Last Updated: 28-01-2026</p>

    <p className="mb-4">
      Welcome to <strong>MEHAR TRADERS INTERNATIONAL LTD</strong> ("we", "our", "us"). Your privacy is
      important to us. This Privacy Policy explains how we collect, use, share, and protect your
      personal data when you use our website and services.
    </p>

    <h2 className="text-xl sm:text-2xl font-semibold mt-6 mb-2">1. Information We Collect</h2>
    <h3 className="text-lg sm:text-xl font-semibold mt-4 mb-1">Information You Provide to Us</h3>
    <ul className="list-disc list-inside mb-4 space-y-1">
      <li>
        <strong>Account details:</strong> Name, email address, phone number, and company information
        (if applicable) when creating an account.
      </li>
      <li>
        <strong>Order information:</strong> Billing and delivery addresses, purchase history, and
        other order-related information needed to fulfil your order.
      </li>
      <li>
        <strong>Contact information:</strong> Details you provide when contacting us via email, phone,
        WhatsApp, Teams, or through our website.
      </li>
    </ul>

    <h3 className="text-lg sm:text-xl font-semibold mt-4 mb-1">Information We Automatically Collect</h3>
    <p className="mb-2">When you visit our website, we may collect:</p>
    <ul className="list-disc list-inside mb-4 space-y-1">
      <li>
        <strong>Usage data:</strong> IP address, browser type, operating system, device identifiers,
        referral URLs, and other technical data.
      </li>
      <li>
        <strong>Cookies:</strong> Small files stored on your device to support website functionality
        and improve your experience (see Section 5).
      </li>
      <li>
        <strong>Location data:</strong> With your permission, approximate location data to improve
        site experience and prevent fraud.
      </li>
    </ul>

    <h2 className="text-xl sm:text-2xl font-semibold mt-6 mb-2">2. How We Use Your Information</h2>
    <p className="mb-2">We use your personal data to:</p>
    <ul className="list-disc list-inside mb-4 space-y-1">
      <li>Process and fulfil orders, including dispatch and delivery updates.</li>
      <li>Provide customer support and manage your account.</li>
      <li>Improve our website, services, and user experience.</li>
      <li>
        Send marketing communications if you have opted in, with the option to opt out at any time.
      </li>
      <li>Comply with legal obligations and support fraud prevention and security.</li>
    </ul>
    <p className="mb-4 font-semibold">We do not sell your personal information.</p>

    <h2 className="text-xl sm:text-2xl font-semibold mt-6 mb-2">3. How We Share Your Information</h2>
    <p className="mb-2">We may share your information with:</p>
    <ul className="list-disc list-inside mb-4 space-y-1">
      <li>
        <strong>Service providers:</strong> Payment processors, delivery partners, IT support,
        hosting, and analytics services.
      </li>
      <li>
        <strong>Legal authorities:</strong> Where required by law or to protect our rights, property,
        or safety.
      </li>
      <li>
        <strong>Third parties:</strong> Only with your explicit consent or when necessary to perform
        a contract with you.
      </li>
    </ul>
    <p className="mb-4 font-semibold">
      We do not sell your personal information to third parties.
    </p>

    <h2 className="text-xl sm:text-2xl font-semibold mt-6 mb-2">4. Children</h2>
    <p className="mb-4">
      Our website is not intended for anyone under 18, and we do not knowingly collect personal data
      from children.
    </p>

    <h2 className="text-xl sm:text-2xl font-semibold mt-6 mb-2">5. Cookies</h2>
    <h3 className="text-lg sm:text-xl font-semibold mt-4 mb-1">What Are Cookies?</h3>
    <p className="mb-4">
      Cookies are small text files stored on your device to support website functionality and improve
      your browsing experience.
    </p>
    <h3 className="text-lg sm:text-xl font-semibold mt-4 mb-1">Types of Cookies We Use</h3>
    <ul className="list-disc list-inside mb-4 space-y-1">
      <li>
        <strong>Essential cookies:</strong> Required for core functions like account login and
        shopping basket.
      </li>
      <li>
        <strong>Performance cookies:</strong> Help us understand website usage and improve
        performance.
      </li>
      <li>
        <strong>Marketing cookies:</strong> Used to personalise content and advertising (if enabled).
      </li>
    </ul>
    <h3 className="text-lg sm:text-xl font-semibold mt-4 mb-1">Managing Cookies</h3>
    <p className="mb-4">
      You can manage or disable cookies via your browser settings. Disabling cookies may impact
      website functionality.
    </p>

    <h2 className="text-xl sm:text-2xl font-semibold mt-6 mb-2">6. Your Rights</h2>
    <p className="mb-2">
      Under data protection laws, you have the right to:
    </p>
    <ul className="list-disc list-inside mb-4 space-y-1">
      <li>
        <strong>Access:</strong> Request a copy of your personal data.
      </li>
      <li>
        <strong>Rectification:</strong> Correct inaccurate or incomplete data.
      </li>
      <li>
        <strong>Erasure:</strong> Request deletion of your data (subject to legal obligations).
      </li>
      <li>
        <strong>Restriction:</strong> Limit how we use your data in certain circumstances.
      </li>
      <li>
        <strong>Data portability:</strong> Request transfer of your data to another provider where
        applicable.
      </li>
      <li>
        <strong>Withdraw consent:</strong> Opt out of marketing communications where consent is the
        basis.
      </li>
      <li>
        <strong>Object:</strong> Object to processing based on legitimate interests in certain
        circumstances.
      </li>
    </ul>
    <p className="mb-4">
      To exercise your rights, contact us at{" "}
      <a href="mailto:info@mehartraders.co.uk" className="text-teal-400 underline">
        info@mehartraders.co.uk
      </a>
      {" "}or{" "}
      <a href="mailto:hello@mehartradersinternational.co.uk" className="text-teal-400 underline">
        hello@mehartradersinternational.co.uk
      </a>
      .
    </p>

    <h2 className="text-xl sm:text-2xl font-semibold mt-6 mb-2">7. Data Security</h2>
    <p className="mb-4">
      We implement technical and organizational measures to protect your data. However, no
      transmission over the internet is completely secure, and absolute security cannot be
      guaranteed.
    </p>

    <h2 className="text-xl sm:text-2xl font-semibold mt-6 mb-2">8. Data Retention</h2>
    <p className="mb-4">
      We retain personal data only as long as necessary for the purposes described, including legal,
      accounting, or reporting requirements.
    </p>

    <h2 className="text-xl sm:text-2xl font-semibold mt-6 mb-2">9. Third-Party Links</h2>
    <p className="mb-4">
      Our website may link to third-party websites. We are not responsible for their privacy
      practices or content.
    </p>

    <h2 className="text-xl sm:text-2xl font-semibold mt-6 mb-2">10. Changes to This Policy</h2>
    <p className="mb-4">
      We may update this Privacy Policy to reflect changes in practices or legal requirements.
      Updates will be posted on this page, with significant changes communicated where appropriate.
    </p>

    <h2 className="text-xl sm:text-2xl font-semibold mt-6 mb-2">11. Contact Us</h2>
    <p className="mb-4">
      If you have questions or concerns, contact:
    </p>
    <p className="mb-4">
      <strong>MEHAR TRADERS INTERNATIONAL LTD</strong>
      <br />
      100 Randall Avenue, London, United Kingdom, NW2 7SU
      <br />
      Email:{" "}
      <a href="mailto:info@mehartraders.co.uk" className="text-teal-400 underline">
        info@mehartraders.co.uk
      </a>
      {" "}or{" "}
      <a href="mailto:hello@mehartradersinternational.co.uk" className="text-teal-400 underline">
        hello@mehartradersinternational.co.uk
      </a>
      <br />
      Phone: +44 7448 739174
    </p>

    <p className="mt-6">
      By placing an order with <strong>MEHAR TRADERS INTERNATIONAL LTD</strong>, you agree to the
      terms of this Privacy Policy.
    </p>
  </div>
);

export default PrivacyPolicy;





